// SPEC_DEMO.md PR-5. Root of the /demo bundle.
//
// Architectural constraint (see SPEC_DEMO.md → Implementation Notes →
// "no in-app navigation"): this component is NEVER mounted inside
// Auth0Provider / TeamProvider / ProjectsProvider. The path-based
// dispatch in main.jsx renders <DemoApp /> for window.location.pathname
// === '/demo' and <App /> otherwise — there is no client-side route
// swap between them. A "Book a demo" CTA uses target="_top" to navigate
// the iframe parent, which is a full origin transition.
//
// What this demo actually does:
//   1. Mounts the same controls a paying user sees, with the locked-
//      filter set (NO only, hydrogen-PEM / data-center-colocation,
//      100–500 MW).
//   2. POSTs to the REAL screening engine at /api/v1/sites/search with
//      `X-Demo-Mode: true`. The backend gate clamps the body to demo-
//      safe ranges and strips per-site enrichment out of the final
//      `complete` SSE event.
//   3. Streams SSE events into <DemoSearchProgress /> so the visitor
//      can watch the actual stages fire — "substations: 8 → teiger:
//      loading from MML → zone_filter: 142 evaluated, 73 pass →
//      enriching → complete: 11 ranked sites".
//   4. Renders streamed result coordinates on the map as they arrive.
//      Clicking one opens <LockedAnalysisPreview />; the real deep-
//      dive panel is reserved for paying users.

import React, { useEffect, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './demo.css';

import DemoControls from './DemoControls';
import DemoMap from './DemoMap';
import DemoSearchProgress from './DemoSearchProgress';
import LockedAnalysisPreview from './LockedAnalysisPreview';
import MobileGate from './MobileGate';
import { demoReplayStream } from './demoApi';

// One-time cleanup: drop the mock-auth localStorage key from any prior
// authed session so demo state can't bleed back in.
const LEGACY_LS_KEY = 'seidr.currentUserId';

const INITIAL_PARAMS = {
  technology: 'hydrogen',
  country: 'NO',
  subtype: 'PEM',
};


export default function DemoApp() {
  // ── Mobile gate ────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 900,
  );
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 900); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Legacy LS cleanup (one-shot) ───────────────────────────────────
  useEffect(() => {
    try { localStorage.removeItem(LEGACY_LS_KEY); } catch { /* ignore */ }
  }, []);

  // ── Demo state ─────────────────────────────────────────────────────
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [error, setError] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const mapRef = useRef(null);

  // Replay state. `progress` is the array of stage events emitted so
  // far; `results` is the final ranked list (populated when `complete`
  // arrives); `loading` is true while the replay is in flight.
  // `runKey` lets <DemoSearchProgress /> remount cleanly on re-run.
  //
  // The demo replays a pre-captured run (frontend/src/demo/demoFixture.json)
  // with compressed timing so the funnel finishes in ~3.5s instead of
  // the engine's real ~20-30s. Capacity is fixed (the slider is gone),
  // so there is no stale-state to track any more.
  const [progress, setProgress] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runKey, setRunKey] = useState(0);
  // -1 = no row/marker focused. Shared between <DemoMap> (the
  // teal numbered markers) and the ranked-sites list inside
  // <DemoSearchProgress> (sidebar) so hovering either side
  // highlights the other.
  const [hoveredResultIdx, setHoveredResultIdx] = useState(-1);
  const abortRef = useRef(null);

  function applyParams(next) {
    const focus = next?.__presetMapFocus;
    const { __presetMapFocus, ...clean } = next;
    setParams(clean);
    if (focus && mapRef.current?.flyTo) {
      mapRef.current.flyTo({
        center: [focus.lon, focus.lat],
        zoom: focus.zoom ?? 6,
        speed: 1.2,
      });
    }
  }

  async function runScreening() {
    // Cancel any in-flight replay so a fast re-run doesn't pile up two
    // schedulers firing into the same progress array.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
    }
    const ac = new AbortController();
    abortRef.current = ac;

    setError(null);
    setSelectedSite(null);
    setProgress([]);
    setResults([]);
    setLoading(true);
    setRunKey((k) => k + 1);

    try {
      await demoReplayStream((event) => {
        setProgress((p) => [...p, event]);
        if (event.stage === 'complete') {
          setResults(event.results || []);
        }
      }, ac.signal);
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setError(e?.message || String(e));
      }
    } finally {
      setLoading(false);
    }
  }

  // Cancel any in-flight stream on unmount so a route change doesn't
  // leave a hanging fetch reader. The demo intentionally does NOT
  // auto-run on mount — screening fires only when the visitor clicks
  // "Run screening", so the slider/preset is a deliberate action.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
      }
    };
  }, []);

  if (isMobile) return <MobileGate />;

  return (
    <div className="demo-app">
      <header className="demo-header">
        <span className="demo-header-logo">SEIDR</span>
        <span className="demo-header-tag">Site Finder · Demo</span>
        <a
          className="demo-header-cta"
          href="https://calendly.com/benjamin-respire/seidr"
          target="_top"
          rel="noopener noreferrer"
        >
          Book a demo
        </a>
      </header>

      <main className="demo-main">
        <aside className="demo-sidebar">
          <DemoControls
            params={params}
            onChange={applyParams}
            onRun={runScreening}
            loading={loading}
            error={error}
          />
          <DemoSearchProgress
            key={runKey}
            events={progress}
            loading={loading}
            error={error}
            results={results}
            hoveredIdx={hoveredResultIdx}
            onHoverIdx={setHoveredResultIdx}
            onSiteClick={setSelectedSite}
          />
        </aside>
        <div className="demo-map-area">
          <DemoMap
            ref={mapRef}
            results={results}
            onSiteClick={setSelectedSite}
            hoveredIdx={hoveredResultIdx}
            onHoverIdx={setHoveredResultIdx}
          />
          {selectedSite && (
            <LockedAnalysisPreview
              site={selectedSite}
              onClose={() => setSelectedSite(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
